import { useMemo, useCallback, SyntheticEvent } from 'react';
import { Location, useLocation } from 'react-router-dom';

import { Link } from '~/utils/navigation';
import { GAME_MODE } from '~/components/casino-shared/constants';
import { authenticationSelector } from '~/components/authentication/ducks/selectors';
import { showNotification } from '~/components/notifications/ducks/notifications';
import { searchConversion } from '~/components/search/ducks/index';
import { isCasinoSearchPath } from '~/utils/pathname';
import useLoginFlow from '~/hooks/use-login-flow';
import useUIContext from '~/ui/use-ui-context';
import useCasinoRoutes from '~/components/router/use-casino-routes';
import useContextualPathTransformer from '~/hooks/use-contextual-path-transformer';
import useIsFunModeAllowed from '~/components/casino-shared/utilities/use-is-fun-mode-allowed';
import { hasDepositLoginSelector, lovedGamesSelector } from '~/components/casino-shared/ducks/selectors';
import { toggleLoveGame } from '~/components/casino-shared/ducks/index';
import { TranslationHash } from '~/components/translation/use-translations';
import {
	ICON_ARROW_LEFT,
	ICON_CASINO_SEVEN,
	ICON_CLOSE,
	ICON_HEART,
	ICON_HEART_FILLED,
	ICON_USER_LOGIN_LINE
} from '~/components/icon/index';
import { useCasinoSharedDispatch, useCasinoSharedSelector } from '~/components/casino-shared/ducks/types';
import { EnhancedGame } from '../types';

import KebabMenu from './kebab-menu';
import PlayButton from './play-button';
import Seats from './seats';
import LastWinningNumbers from './last-winning-numbers';
import { Seat } from './types';
import { StyledIcon } from './game-info.styles';
import MediaSection, { ImageComponent } from './media-section';
import {
	PrimarySection as PrimarySectionStyled,
	HeaderSection,
	TitleSection,
	TitleContainer,
	GameName,
	ProviderName,
	BackgroundImageContainer,
	PrimarySectionContainer,
	ButtonContainer,
	BackIcon
} from './primary-section.styles';
import { RouletteNumber } from '../../live-casino/types';
import useIngameImage from '../utilities/use-ingame-image';

type Props = {
	playGame: (args: { mode: string; event: SyntheticEvent; depositLogin?: boolean }) => void;
	t: TranslationHash;
	desktop: boolean;
	closeGameInfo: () => void;
	lastWinningNumbers?: RouletteNumber[];
	seats?: Seat[];
	game: EnhancedGame;
};

const PrimarySection = ({ playGame, t, desktop, closeGameInfo, seats, lastWinningNumbers, game }: Props) => {
	const dispatch = useCasinoSharedDispatch();
	const uiContext = useUIContext();
	const location = useLocation() as Location<{ gameCode: string }>;
	const gameCode = location?.state?.gameCode;
	const authenticated = useCasinoSharedSelector(authenticationSelector);
	const isFunModeAllowed = useIsFunModeAllowed(game);
	const { initializeLogin } = useLoginFlow();
	const hasDepositLogin = useCasinoSharedSelector(hasDepositLoginSelector);
	const transformPath = useContextualPathTransformer();
	const { ROUTE_CASINO_SUBPROVIDER, ROUTE_CASINO_PROVIDER } = useCasinoRoutes();
	const { srcSet: inGameSrcSet } = useIngameImage(game);

	const lovedGames = useCasinoSharedSelector(lovedGamesSelector);
	const isGameLoved = lovedGames.includes(gameCode);

	const onToggleLoveGameClick = useCallback(() => {
		if (!authenticated) {
			initializeLogin({
				from: location.pathname
			});
			dispatch(
				showNotification({
					message: t.LOVEGAMES_LOGOUT,
					color: 'neutral',
					autoHide: true
				})
			);
			return;
		}
		dispatch(toggleLoveGame(gameCode));
	}, [authenticated, dispatch, gameCode, initializeLogin, location.pathname, t.LOVEGAMES_LOGOUT]);

	const menuItems = useMemo(() => {
		const items: {
			icon: string;
			text: string;
			action: (e: SyntheticEvent) => void;
		}[] = [
			{
				icon: isGameLoved ? ICON_HEART_FILLED : ICON_HEART,
				text: t.ADD_TO_FAVORITES,
				action: () => {
					onToggleLoveGameClick();
				}
			}
		];

		if (isFunModeAllowed) {
			items.push({
				icon: ICON_CASINO_SEVEN,
				text: t.PRACTICE_MODE,
				action: (e: SyntheticEvent) => {
					playGame({ event: e, mode: GAME_MODE.FUN });
					if (isCasinoSearchPath(location.pathname)) dispatch(searchConversion(gameCode));
				}
			});
		}

		if (hasDepositLogin) {
			items.push({
				icon: ICON_USER_LOGIN_LINE,
				text: t.MOBILE_LOGIN_LOGIN_BUTTON,
				action: (e: SyntheticEvent) => {
					playGame({ event: e, mode: GAME_MODE.REAL });
					if (isCasinoSearchPath(location.pathname)) dispatch(searchConversion(gameCode));
				}
			});
		}

		return items;
	}, [
		isGameLoved,
		t.ADD_TO_FAVORITES,
		t.PRACTICE_MODE,
		t.MOBILE_LOGIN_LOGIN_BUTTON,
		isFunModeAllowed,
		hasDepositLogin,
		onToggleLoveGameClick,
		playGame,
		location.pathname,
		dispatch,
		gameCode
	]);

	const providerPageURL = game?.casinoSubProviderVO
		? transformPath(`${ROUTE_CASINO_SUBPROVIDER}/${game?.casinoSubProviderVO?.code}`)
		: transformPath(`${ROUTE_CASINO_PROVIDER}/${game.provider.code}`);

	const showSeats = !!seats?.length;
	const showLastNumbers = !!lastWinningNumbers?.length && !lastWinningNumbers?.some(n => n.value?.includes('|'));

	return (
		<PrimarySectionStyled>
			<BackgroundImageContainer>
				<ImageComponent inGameSrcSet={inGameSrcSet} game={game} />
			</BackgroundImageContainer>
			<PrimarySectionContainer extraContent={showSeats || showLastNumbers}>
				<HeaderSection>
					<ButtonContainer onClick={closeGameInfo}>
						{desktop ? (
							<StyledIcon type={ICON_CLOSE} color="var(--game-info-name-text-color)" />
						) : (
							<BackIcon type={ICON_ARROW_LEFT} color="var(--game-info-name-text-color)" />
						)}
					</ButtonContainer>
					{menuItems.length === 1 ? (
						<ButtonContainer onClick={menuItems[0].action}>
							<StyledIcon type={menuItems[0].icon} color="var(--game-info-name-text-color)" />
						</ButtonContainer>
					) : (
						<KebabMenu items={menuItems} hasRegulationHeader={uiContext.hasStaticHeader} />
					)}
				</HeaderSection>

				<MediaSection inGameSrcSet={inGameSrcSet} game={game} />

				<TitleSection>
					<TitleContainer>
						<GameName>{game?.name}</GameName>
						<ProviderName>
							<Link href={providerPageURL}>
								{game?.casinoSubProviderVO ? game?.casinoSubProviderVO?.name : game?.provider?.name}
							</Link>
						</ProviderName>
					</TitleContainer>

					{showSeats && <Seats seats={seats} />}

					{showLastNumbers && <LastWinningNumbers lastWinningNumbers={lastWinningNumbers} />}
				</TitleSection>

				{desktop && <PlayButton playGame={playGame} t={t} />}
			</PrimarySectionContainer>
		</PrimarySectionStyled>
	);
};

export default PrimarySection;
